<template>
    <HeaderCardsLayout v-if="project" variant="No-Logo">
        <template #headline>{{ project.nameShort }}</template>
        <template #headline-extra>
            <BadgeDropDown
                :label="$t('project-series-status-label')"
                class="text-base font-normal"
                :current-value="getProjectStatusItem(project.status as ProjectStatus)"
                :values="getProjectStatusItems(project.status as ProjectStatus)"
                :status="BadgeStatus.state"
                :tooltip="BadgeStatus.tooltip"
                size="Large-40"
                @change-value="handleBadgeDropdown($event)"
            />
        </template>
        <template #breadcrumb>
            <Breadcrumb :headline="$t('breadcrumb')" :current-page="project.nameLong">
                <BreadcrumbLink :target="{ name: 'projects' }">
                    <ConstructionIcon class="mr-1 shrink-0" />
                    {{ $t('project-wiki') }}
                </BreadcrumbLink>
                <BreadcrumbLink v-if="project.series?.id" :target="getProjectSeriesLink(project.series?.id)">
                    {{ project.series?.nameLong }}
                </BreadcrumbLink>
            </Breadcrumb>
        </template>

        <template #buttons>
            <Button
                variant="Danger-Strong"
                size="Base"
                :disabled-explanation="deleteDisabledExplanation"
                @click="deleteProject"
            >
                <template #icon>
                    <DeleteForeverIcon class="size-5" />
                </template>
                {{ $t('delete') }}
            </Button>
        </template>

        <template #tab-navigation>
            <TabNavigation
                :headline="$t('tab')"
                :items="tabNavigationItems"
                tab-panel-id="eventSeriesTabs"
                :tabs-disabled-text="tabsDisabledText"
                @focus-tab-panel="focusTabPanel"
            />
        </template>

        <template #body>
            <PaddedContent>
                <TabPanel :id="tabPanelId" ref="tabPanelRef" :headline="activeTabHeadline">
                    <RouterView />
                </TabPanel>
            </PaddedContent>
        </template>
    </HeaderCardsLayout>
</template>
<script setup lang="ts">
import BadgeDropDown from '@/components/BadgeDropDown/BadgeDropDown.vue';
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb.vue';
import BreadcrumbLink from '@/components/Breadcrumb/BreadcrumbLink.vue';
import Button from '@/components/Button/Button.vue';
import ConstructionIcon from '@/components/Icon/ConstructionIcon.vue';
import DeleteForeverIcon from '@/components/Icon/DeleteForeverIcon.vue';
import HeaderCardsLayout from '@/components/Layout/HeaderCardsLayout.vue';
import PaddedContent from '@/components/Layout/PaddedContent.vue';
import TabNavigation from '@/components/TabNavigation/TabNavigation.vue';
import TabPanel from '@/components/TabNavigation/TabPanel.vue';
import { useTabNavigation } from '@/components/TabNavigation/useTabNavigation';
import { $t } from '@/plugins/fluent';
import { useHeadSafe } from '@unhead/vue';
import { useFluent } from 'fluent-vue';
import { nanoid } from 'nanoid';
import { computed, provide, ref } from 'vue';
import { useProject } from '../composables/useProject';
import { projectKey } from '../keys';
import { getProjectSeriesLink, getProjectStatusItem, getProjectStatusItems } from '../project';
import { ProjectStatus } from '../types';

const props = defineProps<{ projectId: string }>();
const tabPanelRef = ref();
const tabPanelId = nanoid();
const fluent = useFluent();

const projectId = computed(() => props.projectId);

const {
    project,
    deleteDisabledExplanation,
    changeProjectStatusDisabledExplanation,
    updateProjectStatusLoading,
    updateProjectStatusError,
    updateProjectStatus,
    deleteProject,
    ...rest
} = useProject(projectId);

provide(projectKey, {
    project,
    deleteDisabledExplanation,
    changeProjectStatusDisabledExplanation,
    updateProjectStatusLoading,
    updateProjectStatusError,
    updateProjectStatus,
    deleteProject,
    ...rest,
});

const projectTabs = [
    { route: 'project-details-tab', label: fluent.$t('master-data') },
    { route: 'project-overview-tab', label: fluent.$t('allocations') },
    { route: 'project-variants-tab', label: fluent.$t('project-variants') },
    { route: 'project-org-tab', label: fluent.$t('organisational-structure') },
    { route: 'project-appointment-tab', label: fluent.$t('appointment-and-location') },
    { route: 'project-links-tab', label: fluent.$t('links') },
];

const { tabNavigationItems, activeTabHeadline, tabsDisabledText } = useTabNavigation(projectTabs);

function focusTabPanel() {
    tabPanelRef.value.focus();
}

const BadgeStates = {
    default: { state: 'Default' as const, tooltip: undefined },
    loading: { state: 'Loading' as const, tooltip: $t('status-loading') },
    retry: { state: 'Retry' as const, tooltip: $t('status-retry') },
    disabled: { state: 'Disabled' as const, tooltip: changeProjectStatusDisabledExplanation.value },
};

const BadgeStatus = computed(() => {
    if (changeProjectStatusDisabledExplanation.value) return BadgeStates.disabled;
    if (updateProjectStatusLoading.value) return BadgeStates.loading;
    if (updateProjectStatusError.value) return BadgeStates.retry;
    return BadgeStates.default;
});

async function handleBadgeDropdown(id: string) {
    await updateProjectStatus(id as ProjectStatus);
}

useHeadSafe({ title: () => `${project.value?.nameShort} - ${fluent.$t('project-wiki')}` });
</script>
